<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { isValidMobile } from '@/utils/validators';
import { useCountdown } from '@/composables/useCountdown';
import { authService } from '@/services/authService';
import SliderVerify from '@/components/common/SliderVerify.vue';
import { Phone, MessageSquare, ShieldCheck } from 'lucide-vue-next';

const emit = defineEmits<{
  submit: [payload: { mobile: string; code: string }];
  error: [message: string];
}>();

const mobile = ref('');
const code = ref('');
const agreed = ref(false);
const sliderVerified = ref(false);
// 验证码是否已实际下发：输码与登录的前置闸门（未发送验证码时禁止输码/登录）
const codeSent = ref(false);
const isSending = ref(false);
const mobileShake = ref(false);
const codeShake = ref(false);
const sliderVerifyRef = ref<InstanceType<typeof SliderVerify> | null>(null);
const { remaining, isRunning, start } = useCountdown(60);

// 仅 dev 或 E2E 编译产物保留：E2E 测试通过 window.__e2eSliderVerify 直接置滑块状态。
// 生产编译时整段被 tree-shake 剔除，零运行时影响。
if (import.meta.env.DEV || import.meta.env.VITE_E2E === 'true') {
   
  (window as any).__e2eSliderVerify = (verified: boolean) => {
    sliderVerified.value = verified;
  };
}

const mobileError = computed(() => {
  if (!mobile.value) return '';
  return isValidMobile(mobile.value) ? '' : '请输入正确的11位手机号';
});

const canSendCode = computed(() => {
  return isValidMobile(mobile.value) && sliderVerified.value && !isRunning.value && !isSending.value;
});

const canSubmit = computed(() => {
  return isValidMobile(mobile.value) && codeSent.value && code.value.length === 6 && agreed.value;
});

// 手机号变化时重置滑块验证与验证码发送状态
watch(mobile, () => {
  if (sliderVerified.value) {
    sliderVerified.value = false;
    sliderVerifyRef.value?.reset();
  }
  if (codeSent.value) {
    codeSent.value = false;
    code.value = '';
  }
});

async function handleSendCode() {
  if (!canSendCode.value) return;
  isSending.value = true;
  try {
    const result = await authService.sendCode(mobile.value);
    if (result.success) {
      codeSent.value = true;
      start();
    } else {
      emit('error', result.error || '发送失败');
    }
  } finally {
    isSending.value = false;
  }
}

function handleSubmit() {
  if (!canSubmit.value) {
    if (!agreed.value) {
      emit('error', '请先阅读并同意服务协议和隐私权条款');
    }
    // 触发错误抖动反馈
    if (!isValidMobile(mobile.value)) {
      mobileShake.value = true;
      setTimeout(() => (mobileShake.value = false), 400);
    }
    if (code.value.length !== 6) {
      codeShake.value = true;
      setTimeout(() => (codeShake.value = false), 400);
    }
    return;
  }
  emit('submit', { mobile: mobile.value, code: code.value });
}
</script>

<template>
  <div class="flex flex-col gap-5">
    <div class="flex flex-col gap-1.5">
      <label for="mobile-input" class="text-sm font-medium" style="color: var(--color-title);">手机号</label>
      <div
        class="flex items-center gap-2 px-4 h-12 rounded-md border bg-white transition-all duration-200 focus-glow"
        :class="[mobileError ? 'input-error shake-error' : 'border-[var(--color-border)]']"
      >
        <Phone class="w-4 h-4 shrink-0" :class="mobileError ? 'text-[var(--color-error)]' : 'text-[var(--color-caption)]'" />
        <input
          id="mobile-input"
          v-model="mobile"
          type="tel"
          inputmode="numeric"
          maxlength="11"
          placeholder="请输入手机号"
          aria-label="手机号"
          :aria-invalid="mobileError ? 'true' : 'false'"
          aria-describedby="mobile-error"
          class="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-[var(--color-caption)]"
          @keyup.enter="handleSubmit"
        />
      </div>
      <transition name="error-fade">
        <span v-if="mobileError" id="mobile-error" class="text-xs" style="color: var(--color-error);">{{ mobileError }}</span>
      </transition>
    </div>

    <SliderVerify
      ref="sliderVerifyRef"
      @success="sliderVerified = true"
    />

    <div class="flex flex-col gap-1.5">
      <label for="code-input" class="text-sm font-medium" style="color: var(--color-title);">验证码</label>
      <div class="flex gap-3">
        <div
          class="flex min-w-0 flex-1 items-center gap-2 px-4 h-12 rounded-md border transition-all duration-200 focus-glow"
          :class="[
            codeSent ? 'bg-white' : 'bg-[var(--color-card-bg)] cursor-not-allowed',
            code.length > 0 && code.length < 6 ? 'input-error shake-error' : 'border-[var(--color-border)]',
          ]"
        >
          <MessageSquare class="w-4 h-4 shrink-0" :class="code.length > 0 && code.length < 6 ? 'text-[var(--color-error)]' : 'text-[var(--color-caption)]'" />
          <input
            id="code-input"
            v-model="code"
            type="text"
            inputmode="numeric"
            maxlength="6"
            :disabled="!codeSent"
            :placeholder="codeSent ? '请输入6位验证码' : '请先完成滑块验证并获取验证码'"
            aria-label="验证码"
            :aria-invalid="code.length > 0 && code.length < 6 ? 'true' : 'false'"
            class="min-w-0 flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-[var(--color-caption)] tracking-widest disabled:cursor-not-allowed"
            @keyup.enter="handleSubmit"
          />
        </div>
        <button
          type="button"
          :disabled="!canSendCode"
          :class="[
            'min-w-[120px] shrink-0 h-11 rounded-md text-sm font-medium transition-all duration-200 btn-press',
            canSendCode
              ? 'text-white shadow-sm hover:shadow-md'
              : 'bg-[var(--color-card-bg)] text-[var(--color-caption)] cursor-not-allowed',
          ]"
          :style="canSendCode ? 'background: var(--gradient-primary);' : ''"
          @click="handleSendCode"
        >
          <span v-if="isSending">发送中…</span>
          <span v-else-if="isRunning" class="tabular-nums">{{ remaining }}秒后重试</span>
          <span v-else>获取验证码</span>
        </button>
      </div>
    </div>

    <label
      class="agreement-tile flex items-start gap-2 px-3 py-2.5 rounded-md border cursor-pointer transition-all duration-200"
      :class="agreed ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]' : 'border-[var(--color-divider)] hover:border-[var(--color-primary-light)]'"
    >
      <input
        v-model="agreed"
        type="checkbox"
        class="mt-0.5 w-4 h-4 shrink-0 accent-[var(--color-primary)] cursor-pointer"
      />
      <ShieldCheck v-if="agreed" class="w-3.5 h-3.5 mt-0.5 shrink-0" :style="{ color: 'var(--color-primary)' }" />
      <span class="text-xs leading-relaxed" style="color: var(--color-caption);">
        我已阅读并同意
        <RouterLink
          to="/service-agreement"
          target="_blank"
          class="font-medium hover:underline"
          :style="{ color: 'var(--color-primary)' }"
        >
          《服务协议》
        </RouterLink>
        、
        <RouterLink
          to="/privacy-policy"
          target="_blank"
          class="font-medium hover:underline"
          :style="{ color: 'var(--color-primary)' }"
        >
          《隐私权条款》
        </RouterLink>
      </span>
    </label>

    <button
      type="button"
      :disabled="!canSubmit"
      :class="[
        'mt-2 w-full h-11 rounded-md text-base font-medium text-white shadow-sm transition-all duration-200 btn-press',
        canSubmit ? 'hover:shadow-md cursor-pointer' : 'opacity-50 cursor-not-allowed',
      ]"
      style="background: var(--gradient-primary);"
      @click="handleSubmit"
    >
      登录/注册
    </button>
  </div>
</template>

<style scoped>
.error-fade-enter-active,
.error-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.error-fade-enter-from,
.error-fade-leave-to {
  opacity: 0;
  transform: translateY(-2px);
}
</style>
